import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";

import api from "../../services/api";
import Modal from "../../components/ui/Modal";
import { notify } from "../../components/ui/Toast";
import { readApiError } from "../../utils/apiError";
import { exactTime } from "../../utils/time";
import { Confirm, FormError, ModalFoot, PanelBody, isPinfl, PINFL_LENGTH } from "../../components/ui/panelKit";

const LABELS = { pinfl: "PINFL", group_id: "Guruh" };

/**
 * Who may read and write this client's discussions.
 *
 * A row is one membership, not one person: the same PINFL can hold a
 * membership in two groups, and each covers that group's subtree. Removing a
 * row therefore removes exactly that membership -- the one named in the row --
 * and leaves any other the person holds in this client alone.
 *
 * `groups` is passed down rather than fetched again: it is the same tree the
 * groups panel beside this one already loaded, and the select here has to stay
 * in step with it.
 */
const MembersPanel = ({ clientId, clientName = "", groups = [], onCount }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeRemoved, setIncludeRemoved] = useState(false);

  const [open, setOpen] = useState(false);
  const [pinfl, setPinfl] = useState("");
  const [groupId, setGroupId] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/clients/${clientId}/members/`, {
        params: includeRemoved ? { include_removed: 1 } : undefined,
      });
      const list = res.data || [];
      setRows(list);
      // The stat above counts people who can read the discussions today, so
      // the removed rows this table may be showing are not part of it.
      onCount?.(list.filter((m) => m.is_active).length);
    } catch (err) {
      setError(readApiError(err, LABELS, "Aʼzolar roʻyxatini yuklab boʻlmadi."));
    } finally {
      setLoading(false);
    }
  }, [clientId, includeRemoved, onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const value = pinfl.trim();
    if (!isPinfl(value)) {
      setFormError(`PINFL roʻyxatdan oʻtgan shaxsning ${PINFL_LENGTH} xonali raqami boʻlishi kerak. Hozir ${value.length} belgi kiritilgan.`);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      // No group_id means the root group -- the whole client. Sent as an
      // omission rather than as the root id so the intent stays readable in
      // the request log.
      const payload = groupId ? { pinfl: value, group_id: groupId } : { pinfl: value };
      await api.post(`/clients/${clientId}/members/`, payload);
      notify.success("Aʼzo qoʻshildi");
      setOpen(false);
      setPinfl("");
      setGroupId("");
      await load();
    } catch (err) {
      setFormError(readApiError(err, LABELS, "Aʼzoni qoʻshib boʻlmadi."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      // `group_id` narrows the removal to the membership in this row. Without
      // it the server drops EVERY membership this PINFL holds in the client,
      // which is not what a delete button on one row can be read to mean.
      await api.delete(`/clients/${clientId}/members/${confirm.pinfl}/`, {
        params: { group_id: confirm.group_id },
      });
      notify.success("Aʼzo oʻchirildi");
      setConfirm(null);
      await load();
    } catch (err) {
      notify.error(readApiError(err, LABELS, "Aʼzoni oʻchirib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ms-panel">
      <div className="ms-panel-head">
        <h2>Aʼzolar</h2>
        <div className="ms-toolbar-spacer" />
        {!loading && !rows.some((m) => m.is_active) && (
          <span className="ms-badge ms-badge--progress">aʼzo yoʻq</span>
        )}
        <label className="ms-checkrow">
          <input
            type="checkbox"
            checked={includeRemoved}
            onChange={(e) => setIncludeRemoved(e.target.checked)}
          />
          <i />
          <span>Oʻchirilganlar ham</span>
        </label>
        <button
          type="button"
          className="ms-btn ms-btn--primary ms-btn--sm"
          onClick={() => {
            setPinfl("");
            setGroupId("");
            setFormError("");
            setOpen(true);
          }}
        >
          <Icon icon="tabler:plus" />
          Aʼzo
        </button>
      </div>

      <PanelBody
        loading={loading}
        error={error}
        empty={!rows.length}
        emptyText="Aʼzo yoʻq — hech kim muhokamalarni oʻqiy olmaydi"
      >
        <div className="ms-table-wrap">
          <table className="ms-table ms-table--compact">
            <thead>
              <tr>
                <th style={{ width: 170 }}>PINFL</th>
                <th>Ism</th>
                <th style={{ width: 150 }}>Guruh</th>
                <th style={{ width: 120 }}>Kirgan</th>
                <th className="ms-td-actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className="ms-mono">{m.pinfl}</span>
                  </td>
                  <td className="ms-td-strong">
                    {m.identity_id ? (
                      <Link className="ms-sublink" to={`/shaxslar/${m.identity_id}`}>
                        {m.full_name || "—"}
                      </Link>
                    ) : (
                      m.full_name || "—"
                    )}
                    {m.is_auto && (
                      <span
                        className="ms-badge ms-badge--neutral"
                        style={{ marginInlineStart: 6 }}
                        title="Mijoz tizimi orqali birinchi kirishda avtomatik qoʻshilgan"
                      >
                        avto{clientName ? ` · ${clientName}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="ms-td-clamp" title={m.group_name}>
                    {m.group_name}
                    {m.is_root_group && (
                      <span className="ms-badge ms-badge--neutral" style={{ marginInlineStart: 6 }}>
                        butun tizim
                      </span>
                    )}
                  </td>
                  <td>
                    {!m.is_active ? (
                      <span
                        className="ms-badge ms-badge--danger"
                        title={exactTime(m.removed_at)}
                      >
                        oʻchirilgan
                      </span>
                    ) : m.has_signed_in ? (
                      <span className="ms-badge ms-badge--success">ha</span>
                    ) : (
                      <span className="ms-badge ms-badge--neutral">hali yoʻq</span>
                    )}
                  </td>
                  <td className="ms-td-actions">
                    {m.is_active && (
                      <div className="ms-rowacts">
                        <button
                          type="button"
                          className="ms-rowact is-danger"
                          title="Oʻchirish"
                          aria-label={`${m.pinfl} aʼzoligini oʻchirish`}
                          onClick={() => setConfirm(m)}
                        >
                          <Icon icon="tabler:user-minus" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelBody>

      <Modal
        open={open}
        title="Aʼzo qoʻshish"
        size="sm"
        onClose={() => setOpen(false)}
        footer={
          <ModalFoot
            onCancel={() => setOpen(false)}
            onSave={add}
            saving={saving}
            saveLabel="Qoʻshish"
            savingLabel="Qoʻshilmoqda..."
          />
        }
      >
        <div className="ms-form-grid ms-form-grid--stack">
          <div className="ms-field ms-field--full">
            <label htmlFor="ms-member-pinfl">
              PINFL <span>*</span>
            </label>
            <input
              id="ms-member-pinfl"
              type="text"
              inputMode="numeric"
              maxLength={PINFL_LENGTH}
              placeholder="14 xonali raqam"
              className="ms-mono"
              value={pinfl}
              autoFocus
              onChange={(e) => setPinfl(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <span className="ms-field-hint">
              {pinfl.length}/{PINFL_LENGTH}. Shaxs MySuhbatga hech qachon kirmagan
              boʻlsa ham qoʻshish mumkin: u OneID bilan kirganda aʼzoligi shu
              PINFL boʻyicha topiladi.
            </span>
          </div>

          <div className="ms-field ms-field--full">
            <label htmlFor="ms-member-group">Guruh</label>
            <select
              id="ms-member-group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">Butun tizim (ildiz guruh)</option>
              {groups
                .filter((g) => !g.is_root)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {"— ".repeat(Math.max(0, g.depth - 1))}
                    {g.name}
                  </option>
                ))}
            </select>
            <span className="ms-field-hint">
              Aʼzolik tanlangan guruh va uning ostidagi barcha guruhlarni
              qamrab oladi.
            </span>
          </div>

          <FormError>{formError}</FormError>
        </div>
      </Modal>

      <Confirm
        open={!!confirm}
        busy={busy}
        name={confirm ? `${confirm.pinfl}${confirm.full_name ? ` — ${confirm.full_name}` : ""}` : ""}
        title="Aʼzoni oʻchirish"
        message={
          confirm
            ? `“${confirm.group_name}” guruhidagi aʼzolik olib tashlanadi va bu shaxs keyingi soʻrovidan boshlab muhokamalarni koʻrmaydi. Aʼzolik tarixi saqlanadi, lekin qaytarish uchun uni qaytadan qoʻshish kerak.`
            : ""
        }
        onConfirm={remove}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default MembersPanel;
